<template>
  <div class="tickets-table">
    <!-- Loading state -->
    <div v-if="isLoading && tickets.length === 0" class="loading-container">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <span>{{ t('Carregando chamados...') }}</span>
    </div>

    <!-- Empty state -->
    <div v-else-if="!isLoading && tickets.length === 0" class="empty-state">
      <i class="pi pi-inbox" style="font-size: 3rem; color: var(--text-color-secondary)"></i>
      <h3>{{ t('Nenhum chamado encontrado') }}</h3>
      <p>{{ t('Você ainda não abriu nenhum chamado de suporte.') }}</p>
      <Button
        :label="t('Abrir Novo Chamado')"
        icon="pi pi-plus"
        data-testid="support-empty-open-ticket-button"
        @click="openNewTicket"
      />
    </div>

    <!-- Data table -->
    <data-table
      v-else
      :config="datatableConfig"
      :data="tickets"
      @edit-click="handleIdClick"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useOdooTicket } from '../../composables/useOdooTicket';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { log } from '../../utils/DebugLogger';

interface TicketRow {
  id: number | string;
  name?: string;
  stage_name?: string;
  created_at?: string;
  user_description?: string;
  page_uri?: string;
  [key: string]: unknown;
}

interface CellParams {
  value: unknown;
  data: TicketRow;
  node: Record<string, unknown>;
}

interface ButtonClickPayload {
  value: number | string;
  data: TicketRow;
  action: string;
}

const { t } = useI18n();
const { tickets, isLoading, fetchTickets } = useOdooTicket();
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter();

const emit = defineEmits<{
  (e: 'view-ticket', ticket: TicketRow): void;
}>();

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const getStatusSeverity = (status?: string): string => {
  if (!status) return 'info';
  const statusLower = status.toLowerCase();

  if (
    statusLower.includes('resolvido') ||
    statusLower.includes('solved') ||
    statusLower.includes('fechado') ||
    statusLower.includes('closed')
  ) {
    return 'success';
  }
  if (
    statusLower.includes('em andamento') ||
    statusLower.includes('in progress') ||
    statusLower.includes('aguardando')
  ) {
    return 'warning';
  }
  if (statusLower.includes('cancelado') || statusLower.includes('cancelled')) {
    return 'danger';
  }
  return 'info';
};

const truncateUrl = (url: string): string => {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    if (path.length > 30) {
      return '...' + path.substring(path.length - 27);
    }
    return path || '/';
  } catch {
    return url.length > 30 ? '...' + url.substring(url.length - 27) : url;
  }
};

const renderStatus = (params: CellParams): string => {
  const row = params.data;
  const label = row.stage_name || t('Novo');
  const severity = getStatusSeverity(row.stage_name);
  return `<span class="p-tag p-tag-${escapeHtml(severity)}"><span class="p-tag-value">${escapeHtml(label)}</span></span>`;
};

const renderCreatedAt = (params: CellParams): string => {
  const row = params.data;
  if (!row.created_at) return '';
  const dt = fromApiDateTimeFormat(row.created_at);
  return dt ? escapeHtml(formatDateTime(dt)) : '';
};

const renderDescription = (params: CellParams): string => {
  const row = params.data;
  const html = row.user_description || '';
  return `<div class="description-content">${html}</div>`;
};

const renderPageUri = (params: CellParams): string => {
  const row = params.data;
  if (!row.page_uri) {
    return '<span class="no-page">-</span>';
  }
  const safeUrl = escapeHtml(row.page_uri);
  const safeText = escapeHtml(truncateUrl(row.page_uri));
  return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="page-link">${safeText} <i class="pi pi-external-link" style="font-size: 0.75rem; margin-left: 4px"></i></a>`;
};

const tableColumns = computed(() => [
  {
    data: 'id',
    title: t('ID'),
    renderType: 'clickable_id'
  },
  {
    data: 'name',
    title: t('Assunto')
  },
  {
    data: 'stage_name',
    title: t('Status'),
    cellRenderer: renderStatus
  },
  {
    data: 'created_at',
    title: t('Data'),
    cellRenderer: renderCreatedAt
  },
  {
    data: 'user_description',
    title: t('Descrição'),
    cellRenderer: renderDescription
  },
  {
    data: 'page_uri',
    title: t('Página'),
    sortable: false,
    cellRenderer: renderPageUri
  },
  {
    data: 'id',
    title: t('Ações'),
    sortable: false,
    renderType: 'clickable_button',
    buttonText: '',
    buttonIcon: 'pi pi-eye',
    buttonClass: 'p-button-text p-button-sm',
    buttonAction: 'view'
  }
]);

const datatableConfig = computed(() => ({
  tableId: 'ticketsTable',
  columns: tableColumns.value,
  pagination: true,
  pageLength: 10
}));

const loadData = async (): Promise<void> => {
  log('TicketsTable', 'loadData', 'Loading tickets');
  const result = await fetchTickets();
  log('TicketsTable', 'loadData', 'Result', result);
};

const findTicketById = (id: number | string): TicketRow | undefined => {
  return (tickets.value as TicketRow[]).find((tkt) => String(tkt.id) === String(id));
};

const handleIdClick = (id: number | string): void => {
  log('TicketsTable', 'handleIdClick', 'ID clicked', id);
  const ticket = findTicketById(id);
  if (ticket) emit('view-ticket', ticket);
};

const handleButtonClick = ({ value, data, action }: ButtonClickPayload): void => {
  log('TicketsTable', 'handleButtonClick', 'Button clicked', { value, action });
  if (action === 'view') {
    const ticket = data ?? findTicketById(value);
    if (ticket) emit('view-ticket', ticket);
  }
};

const openNewTicket = (): void => {
  eventBus.emit('modal.support-ticket.open');
};

const handleTicketReplied = (): void => {
  log('TicketsTable', 'handleTicketReplied', 'Ticket replied, refreshing');
  void loadData();
};

onMounted(() => {
  log('TicketsTable', 'mounted', 'Component mounted');
  void loadData();
  eventBus.on('ticket.replied', handleTicketReplied);
  eventBus.on('ticket.created', loadData);
});

onBeforeUnmount(() => {
  eventBus.off('ticket.replied', handleTicketReplied);
  eventBus.off('ticket.created', loadData);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.tickets-table {
  background: var(--surface-card);
  border-radius: 8px;
  padding: 1rem;
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  gap: 1rem;
}

.empty-state h3 {
  margin: 0;
  color: var(--text-color);
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

:deep(.description-content) {
  color: var(--text-color);
  font-size: 0.875rem;
  line-height: 1.5;
  max-height: 150px;
  overflow-y: auto;
}

:deep(.description-content p) {
  margin: 0 0 0.5rem 0;
}

:deep(.description-content p:last-child) {
  margin-bottom: 0;
}

:deep(.description-content ul),
:deep(.description-content ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

:deep(.description-content a) {
  color: var(--primary-color);
}

:deep(.page-link) {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
}

:deep(.page-link:hover) {
  text-decoration: underline;
}

:deep(.no-page) {
  color: var(--text-color-secondary);
}
</style>
